import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { requestPasswordReset } from '@/api/endpoints/auth';
import { errorMessage } from '@/api/errors';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (caught) {
      // Only a transport or rate-limit failure reaches here. The endpoint itself
      // answers the same way for a real address and an unknown one.
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="If that address has an account, a reset link is on its way."
        footer={
          <Link to="/login" className="font-medium text-accent hover:text-accent-hover">
            Back to sign in
          </Link>
        }
      >
        <div className="flex items-start gap-3 rounded-[--radius-control] bg-ok-soft px-3 py-3 text-[13px] text-ink-secondary">
          <MailCheck className="mt-0.5 size-4 shrink-0 text-ok" aria-hidden />
          {/* Deliberately not "we sent you an email" — saying so would confirm the
              address has an account to anyone who typed it in. */}
          <p>
            The link works once and expires in 30 minutes. If nothing arrives, check
            your spam folder, then try again.
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to choose a new one."
      footer={
        <Link to="/login" className="font-medium text-accent hover:text-accent-hover">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        {error && (
          <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
